import classnames from 'classnames';
import { forwardRef, memo } from 'react';
import type React from 'react';
import { Spinner } from 'reactstrap';
import useSelectionAreaWrapper from './useSelectionAreaWrapper';
import './styles.scss';

export type SelectionAreaWrapperHandle = {
  resetSelection: () => void;
};

export type SelectionAreaWrapperProps = {
  id: string;
  handleSetSelected: (selected: Set<string>) => void;
  className: string;
  selectables: string;
  isNoData: boolean;
  isLoading: boolean;
  couldForceReset?: boolean;
  defaultSelectedId?: string;
  isEmptySelected?: boolean;
  children: (selected: Set<string>) => React.ReactNode;
};

/**
 * Wraps a drag-selectable region of report items, rendering its children with the current selection. Takes the root ref and selection from useSelectionAreaWrapper, and shows a spinner while isLoading or an empty-state message when isNoData instead of the children.
 *
 * @param props - Wrapper props carrying id, className, isNoData, isLoading and the children render function.
 * @param ref - Forwarded handle exposing the selection area API.
 * @returns The rendered selection area, or its loading or empty state.
 */
const SelectionAreaWrapper = forwardRef<SelectionAreaWrapperHandle, SelectionAreaWrapperProps>(
  (
    {
      id,
      handleSetSelected,
      className,
      selectables,
      isNoData,
      isLoading,
      couldForceReset,
      defaultSelectedId,
      isEmptySelected,
      children,
    },
    ref,
  ) => {
    const { root, selected } = useSelectionAreaWrapper(
      {
        id,
        handleSetSelected,
        className,
        selectables,
        isNoData,
        isLoading,
        couldForceReset,
        defaultSelectedId,
        isEmptySelected,
        children,
      },
      ref,
    );

    return (
      <div id={id} ref={root} className={classnames('selection-area-wrapper', className)}>
        {isLoading ? (
          <div className={classnames('loading')}>
            {/* eslint-disable-next-line react/jsx-curly-brace-presence -- must be a
                  literal empty string, not omitted: reactstrap's Spinner defaultProps
                  falls back to children="Loading..." when children is undefined */}
            <Spinner className="spinner-custom">{''}</Spinner>
          </div>
        ) : isNoData ? (
          <div className={classnames('no-data')}>There is no data to display</div>
        ) : (
          children(selected.current)
        )}
      </div>
    );
  },
);

export default memo(SelectionAreaWrapper);
